'use client';

import { useRouter } from 'next/navigation';

import { useSurface } from '@/components/global/SurfaceProvider';
import {
  DEFAULT_WORKSPACE_SECTION,
  getStoredWorkspaceSection,
} from '@/lib/session/default-surface';
import { t } from '@/lib/design/tokens';

type ToggleTarget = 'deck' | 'workspace';

const TOGGLE_TARGETS: Array<{ id: ToggleTarget; label: string }> = [
  { id: 'deck', label: 'Deck' },
  { id: 'workspace', label: 'Workspace' },
];

export function TopLevelToggle() {
  const surface = useSurface();
  const router = useRouter();

  const navigateTo = (target: ToggleTarget) => {
    if (target === 'deck') {
      router.push('/');
      return;
    }
    const section = getStoredWorkspaceSection() || DEFAULT_WORKSPACE_SECTION;
    router.push(`/${section}`);
  };

  return (
    <div
      role="tablist"
      aria-label="Top-level surface"
      style={{
        display: 'inline-flex',
        gap: 4,
        padding: 4,
        borderRadius: 999,
        background: t.bg1,
        border: `1px solid ${t.border}`,
        fontFamily: t.font,
      }}
    >
      {TOGGLE_TARGETS.map((target) => {
        const active = surface === target.id;
        return (
          <button
            key={target.id}
            type="button"
            role="tab"
            aria-pressed={active}
            aria-selected={active}
            onClick={() => navigateTo(target.id)}
            style={{
              padding: '6px 14px',
              borderRadius: 999,
              border: 'none',
              background: active ? t.card : 'transparent',
              color: active ? t.text1 : t.text3,
              fontFamily: t.font,
              fontSize: 13,
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'color 150ms ease, background 150ms ease',
              boxShadow: active ? t.shadowSm : 'none',
            }}
          >
            {target.label}
          </button>
        );
      })}
    </div>
  );
}
